import { Tabs, Tab, Button, Tooltip, TextField, MenuItem } from '@mui/material';
import { useEffect, useState } from 'react';
import { InfoOutlined } from '@mui/icons-material';
import { useFilter } from '../contexts/FilterContext';

type SkillItem = {
  skill: string;
  category: string;
  growthRate: string;
  relativeDemand: number;
  jobRoles: string[];
  sectors?: string[];
  occupations?: string[];
};
const commonSx = {
  width: '250px',
  '& .MuiOutlinedInput-root': {
    color: '#646A77',
    '& fieldset': {
      borderColor: '#646A77',
    },
    '&:hover fieldset': {
      borderColor: '#646A77',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#646A77',
    },
  },
  '& .MuiSelect-select': {
    color: '#646A77', // selected text color
  },
  '& .MuiSelect-icon': {
    color: '#646A77', // arrow icon color
  },
  '& .MuiInputLabel-root': {
    color: '#646A77',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#646A77',
  },
  '& .MuiSelect-select em': {
    color: '#646A77',
    fontStyle: 'normal', // optional: remove italic style on placeholder
    opacity: 1,
  }
};

const SkillTable: React.FC = () => {
  const { selectedSector, selectedOccupation, timeRange, selectedTab } = useFilter();
  const [page, setPage] = useState(1);
  const [tabIndex, setTabIndex] = useState(0);
  const [skillItemsData, setSkillItemsData] = useState<SkillItem[]>([]);
  const itemsPerPage = 20;
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
const [selectedCategory, setSelectedCategory] = useState('');
const [hasNewSkills, setHasNewSkills] = useState(false);


useEffect(() => {
  const loadSkillsData = async () => {
    try {
      const jsonModule = await import(`../json/${selectedTab}/skillsData.json`);
      const data = jsonModule.default as SkillItem[];
      setSkillItemsData(data);
      setHasNewSkills(data.some(skill => (skill as SkillItem & { new?: boolean }).new === true));    } catch (err) {
      console.error(`Failed to load data for tab: ${selectedTab}`, err);
      setSkillItemsData([]);
      setHasNewSkills(false);
    }
  };

  loadSkillsData();
}, [selectedTab]);



  const parseGrowth = (growthRate: string): number => {
    const parsed = parseFloat(growthRate.replace('%', '').replace('+', ''));
    return isNaN(parsed) ? 0 : parsed;
  };

  const getGrowthColor = (value: number) => {
    if (value > 0) return '#2F7C0D';
    if (value < 0) return '#931035';
    return '#875E00';
  };

  const getFilteredData = () => {
    let filtered = [...skillItemsData];
  switch (tabIndex) {
    case 1:
      filtered = filtered.filter(skill => parseGrowth(skill.growthRate) >= 5);
      break;
    case 2:
      filtered = filtered.filter(skill => parseGrowth(skill.growthRate) <= -5);
      break;
    case 3:
      filtered = filtered.filter(skill => (skill as SkillItem & { new?: boolean }).new === true);
      break;
  }
      if (selectedCategory) {
    filtered = filtered.filter(skill =>
      skill.category
        .split(/,| and /i)
        .map(cat => cat.trim())
        .includes(selectedCategory)
    );
  }
  // Filter by selected sector
  if (selectedSector && selectedSector !== 'All') {
    filtered = filtered.filter(skill => skill.sectors?.includes(selectedSector));
  }

  // Filter by selected occupation
  if (selectedOccupation && selectedOccupation !== 'All') {
    filtered = filtered.filter(skill => skill.occupations?.includes(selectedOccupation));
  }


  return filtered;
  };

  const filteredData = getFilteredData();
const sortedData = [...filteredData].sort((a, b) => {
  const aValue = parseGrowth(a.growthRate);
  const bValue = parseGrowth(b.growthRate);
  return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
});

const displayedData = sortedData.slice((page - 1) * itemsPerPage, page * itemsPerPage);


    const handleSortByGrowth = () => {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    };


const allCategories = Array.from(
  new Set(
    skillItemsData
      .flatMap((item) =>
        item.category
          .split(/,| and /i)
          .map((cat) => cat.trim())
      )
      .filter((cat) => cat.length > 0)
  )
).sort();


  return (
    <div className="flex-grow-1 border rounded p-3 bg-white" style={{ minWidth: '300px', flex: 1 }}>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center pb-4">
        <div>
          <h6 className="mb-1 d-flex align-items-center gap-1" style={{ fontSize: 20 }}>
            Top Skills
            <Tooltip title="Shows skills in demand and their growth rate" arrow>
              <InfoOutlined sx={{ fontSize: 16, color: '#6b7280', cursor: 'pointer' }} />
            </Tooltip>
          </h6>
          <p className="text-muted small mb-0">Click a skill to filter the dashboard by related roles.</p>
        </div>
        {selectedTab==="PulseCast"&&<div>
          <TextField
  select
  size="small"
  label="Category"
  value={selectedCategory}
  onChange={(e) => setSelectedCategory(e.target.value)}
  sx={commonSx}
>
  <MenuItem value="">
    <em>Choose category...</em>
  </MenuItem>
  {allCategories.map((category) => (
    <MenuItem key={category} value={category}>
      {category}
    </MenuItem>
  ))}
</TextField>
        </div>}
      </div>

      {/* Tabs */}
      <Tabs
        value={tabIndex}
        onChange={(e, newValue) => {
          setTabIndex(newValue);
          setPage(1);
        }}
        variant="fullWidth"
        TabIndicatorProps={{ style: { display: 'none' } }}
        sx={{
          borderBottom: '1px solid #D7DAE0',
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: 14,
            color: '#374151',
            borderBottom: '1px solid #D7DAE0',
            borderRadius: 0,
            minWidth: 0,
            '&:hover': {
              color: '#043C5B',
              fontWeight: 600,
              borderBottom: '1px solid #043C5B',
              background: '#F3F5F7',
            },
          },
          '& .Mui-selected': {
            color: '#043C5B',
            fontWeight: 600,
            borderBottom: '1px solid #043C5B',
            background: '#F3F5F7',
          },
        }}
      >
        <Tab label="All" />
        <Tab label="Growing" />
        <Tab label="Declining" />
        <Tab label="New" disabled={!hasNewSkills} />
      </Tabs>

      {/* Table */}
      <div className="table-responsive" style={{ maxHeight: 'auto', overflowY: 'auto' }}>
        <table className="table table-sm mb-0">
          <thead>
            <tr style={{ color: '#374151', fontSize: '14px' }}>
              <th>Skill</th>
              {selectedTab==="PulseCast"&&<th>Category</th>}
              <th>Job Roles</th>
              <th>Relative Demand</th>
              <th onClick={handleSortByGrowth} style={{ cursor: 'pointer' }}>
                  Growth Rate { sortOrder === 'asc' ? '↑' : '↓'}
                </th>
            </tr>
          </thead>
          <tbody>
            {displayedData.map((skill, index) => (
              <tr key={index} style={{ fontSize: '14px' }}>
                <td style={{ backgroundColor: '#fff', color: '#4B5563', fontSize: 15, padding: '10px' }}>
                  <strong>{skill.skill}</strong>
                </td>
                
              {selectedTab==="PulseCast"&&<td style={{ backgroundColor: '#fff', color: '#4B5563', fontSize: 15, padding: '10px' }}>
                  {skill.category}
                </td>}
                <td style={{ backgroundColor: '#fff', color: '#4B5563', fontSize: 15, padding: '10px' }}>
                  {skill.jobRoles.join(', ')}
                </td>
                <td style={{ backgroundColor: '#fff', color: '#4B5563', fontSize: 15, padding: '10px' }}>
                  {skill.relativeDemand}
                </td>
                <td style={{ backgroundColor: '#fff', color: getGrowthColor(parseGrowth(skill.growthRate)), fontSize: 15, padding: '10px' }}>
                  {skill.growthRate}
                </td>
              </tr>
            ))}
            {displayedData.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted py-3 bg-white">
                  No data available for this category.
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="d-flex justify-content-center mt-3">
          {(() => {
            const totalPages = Math.ceil(filteredData.length / itemsPerPage);
            const paginationItems: (number | string)[] = [];

            if (totalPages <= 5) {
              for (let i = 1; i <= totalPages; i++) paginationItems.push(i);
            } else {
              paginationItems.push(1);
              if (page > 3) paginationItems.push('...');
              const start = Math.max(2, page - 1);
              const end = Math.min(totalPages - 1, page + 1);
              for (let i = start; i <= end; i++) paginationItems.push(i);
              if (page < totalPages - 2) paginationItems.push('...');
              paginationItems.push(totalPages);
            }

            return paginationItems.map((item, idx) =>
              item === '...' ? (
                <span key={`dots-${idx}`} style={{ padding: '0 6px', color: '#6b7280' }}>…</span>
              ) : (
                <Button
                  key={item}
                  variant={item === page ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setPage(Number(item))}
                  sx={{
                    mx: 0.5,
                    minWidth: 30,
                    width: 30,
                    height: 30,
                    borderRadius: '50%',
                    padding: 0,
                    boxShadow: 'none',
                    backgroundColor: item === page ? '#043C5B' : 'transparent',
                    color: item === page ? '#fff' : '#374151',
                    borderColor: '#D7DAE0',
                    '&:hover': {
                      backgroundColor: item === page ? '#032f49' : '#f3f3f3',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {item}
                </Button>
              )
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default SkillTable;
